import type { Question, ResponseValue } from '@/core/types';
import { normalizeAnswer } from '@/core/util/misc';
import { evaluate, evaluateRubric } from './evaluate';
import { VALIDATORS } from './validators';

/**
 * CONTENT VALIDATION — every generated question passes these checks before a
 * student ever sees it. Invalid questions are discarded and regenerated.
 */
export function validateQuestion(q: Question): string[] {
  const issues: string[] = [];
  const r = q.response;
  const a = q.answer;
  if (!q.prompt?.trim()) issues.push('empty prompt');
  if (q.difficulty < 1 || q.difficulty > 7) issues.push('difficulty out of range');

  const uniqueLabels = (labels: string[], what: string) => {
    const seen = new Set<string>();
    for (const l of labels) {
      const k = normalizeAnswer(l);
      if (seen.has(k)) issues.push(`duplicate ${what}: "${l}"`);
      seen.add(k);
    }
  };

  if (r.type === 'choice') {
    if (r.options.length < 2) issues.push('fewer than 2 options');
    uniqueLabels(r.options.map((o) => o.label), 'option');
    if (a.type !== 'choice' || !r.options.some((o) => o.id === a.correct)) issues.push('correct option missing');
  }
  if (r.type === 'multi') {
    uniqueLabels(r.options.map((o) => o.label), 'option');
    if (a.type !== 'multi' || !a.correct.length || a.correct.some((c) => !r.options.some((o) => o.id === c))) issues.push('multi answer invalid');
  }
  if (a.type === 'numeric' && !Number.isFinite(a.value)) issues.push('numeric answer not finite');
  if (a.type === 'text' && !a.accepted.length) issues.push('no accepted text');
  if (r.type === 'match') {
    uniqueLabels(r.left.map((o) => o.label), 'left label');
    uniqueLabels(r.right.map((o) => o.label), 'right label');
    if (a.type !== 'match' || Object.keys(a.pairs).length !== r.left.length) issues.push('match pairs incomplete');
  }
  if (r.type === 'order') {
    if (r.items.length < 2) issues.push('order needs 2+ items');
    uniqueLabels(r.items.map((o) => o.label), 'order item');
  }
  if (r.type === 'categorize') {
    if (a.type !== 'categorize') issues.push('categorize answer missing');
    else {
      const used = new Set(Object.values(a.assignment));
      if (used.size < 2) issues.push('categorize uses fewer than 2 categories');
      if (Object.values(a.assignment).some((c) => !r.categories.some((k) => k.id === c))) issues.push('unknown category');
    }
    uniqueLabels(r.items.map((o) => o.label), 'item');
  }
  if (r.type === 'tokens' && a.type === 'tokens') {
    const tokenLabels = r.tokens.map((t) => t.label);
    const words = a.sentences[0].split(' ');
    const pool = [...tokenLabels];
    for (const w of words) {
      const i = pool.indexOf(w);
      if (i < 0) issues.push(`token "${w}" missing`);
      else pool.splice(i, 1);
    }
  }
  if (a.type === 'validator') {
    const v = VALIDATORS[a.validatorId];
    if (!v) issues.push(`unknown validator ${a.validatorId}`);
    else {
      const example = a.display.replace(/^(e\.g\.|for example|f\.eks\.|for eksempel)\s*/i, '');
      const res = v(example, a.params);
      if (!res.ok) issues.push(`validator rejects its own example answer: ${example} (${res.feedback})`);
    }
  }
  if (a.type === 'rubric') {
    const res = evaluateRubric(a, a.exemplar, a.exemplar);
    if (!res.correct) issues.push('rubric exemplar does not pass its own rubric');
  }

  // Self-consistency: the canonical correct response must evaluate as correct.
  const canon = canonicalResponse(q);
  if (canon) {
    const ev = evaluate(q, canon);
    if (!ev.correct) issues.push(`canonical answer evaluates as wrong (${ev.feedback})`);
  }
  return issues;
}

/** Build the response a perfect student would give, from the answer key. */
export function canonicalResponse(q: Question): ResponseValue | null {
  const a = q.answer;
  switch (a.type) {
    case 'choice': return { type: 'choice', value: a.correct };
    case 'multi': return { type: 'multi', value: [...a.correct] };
    case 'numeric': return { type: 'numeric', value: String(a.value) };
    case 'text': return { type: 'text', value: a.accepted[0] };
    case 'match': return { type: 'match', value: { ...a.pairs } };
    case 'order': return { type: 'order', value: [...a.sequence] };
    case 'categorize': return { type: 'categorize', value: { ...a.assignment } };
    case 'steps': return { type: 'steps', value: Object.fromEntries(Object.entries(a.values).map(([k, v]) => [k, v.value !== undefined ? String(v.value) : v.accepted?.[0] ?? ''])) };
    case 'tokens': {
      if (q.response.type !== 'tokens') return null;
      const pool = [...q.response.tokens];
      const ids: string[] = [];
      for (const w of a.sentences[0].split(' ')) {
        const i = pool.findIndex((t) => t.label === w);
        if (i < 0) return null;
        ids.push(pool[i].id);
        pool.splice(i, 1);
      }
      return { type: 'tokens', value: ids };
    }
    case 'tf-reason': return { type: 'tf-reason', truth: a.truth, reason: a.reason };
    case 'pair': return { type: 'pair', first: a.first, second: a.second };
    case 'rubric': return null;
    case 'validator': return null;
  }
}
